// LocalStoreRegistration.tsx
import React, { useEffect, useRef, useState } from 'react';
import './LocalStoreRegistration.scss';
import {
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  IconButton
} from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import StorefrontIcon from '@material-ui/icons/Storefront';
import HistoryIcon from '@material-ui/icons/History';
import EventIcon from '@material-ui/icons/Event';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import PaymentIcon from '@material-ui/icons/Payment';
import ReceiptIcon from '@material-ui/icons/Receipt';
import { http, localstorage, formatService, AlertService, useNavigationService } from '../fx-core';
import LocalStoreAnalytics from '../LocalStore/LocalStoreAnalytics';
import LocalStoreBookingCalendar, { CalendarSlot } from './LocalStoreBookingCalendar';

interface SpecialCommunityOption {
  id: number;
  text: string;
  code?: string;
}

interface SlotDto {
  id?: number;
  slotDate: string;
  slotLabel: string;
  activationStatus?: string;
}

interface StallOption {
  id: number;
  communityName: string;
  bookingType: string;
  storeName?: string;
  slots: SlotDto[];
}

interface PendingApprovalOption {
  id: number;
  communityName: string;
  bookingType: string;
  slots: SlotDto[];
  amountPaid: number;
  status: string;
  rejectedReason?: string;
}

interface MerchantOrder {
  id: number;
  localStoreBookingId: number;
  orderGroupId?: string;
  expiresAt?: string;
  storeName: string;
  communityName: string;
  productName: string;
  buyerName: string;
  buyerMobile: string;
  buyerFlatNumber?: string;
  quantity: number;
  price: number | null;
  fulfilmentType: string;
  status: string;
  reservedDate: string;
}

const ORDER_STATUS_LABELS: Record<string, string> = {
  Reserved: 'Reserved', PickedUp: 'Picked up', Delivered: 'Delivered', NoShow: "Didn't show"
};

// Maps backend error codes from `localstore/register/custom` to friendlier,
// actionable copy. Falls back to the raw code (or a generic message) if a
// code isn't listed here yet.
const REGISTER_ERROR_MESSAGES: Record<string, string> = {
  SLOT_IN_PAST: "One of your selected times has already passed — please pick your slots again.",
  SLOT_TAKEN: "One of your selected slots was just booked by someone else — please pick again.",
  SLOTS_OVERLAP_EACH_OTHER: "Two of your selected slots overlap — please review your picks.",
  NO_SLOTS: "Please select at least one time slot.",
  INVALID_COUPON: "That coupon is invalid or no longer available. Please remove it or try another code.",
  COUPON_ALREADY_USED_THIS_MONTH: "You have already used this coupon this month.",
  INVALID_AMOUNT: "The booking amount changed. Please reopen the payment form and apply the coupon again."
};

const PAYMENT_QR_IMAGE = "https://d11w9j7qb25q56.cloudfront.net/test-uploads/SCI+-QR_Image.jpeg";
const DEFAULT_HOURLY_RATE = 200;

const LocalStoreRegistration = (props: any) => {
  const { back, navigate } = useNavigationService();

  const navToManageStore = (bookingId: number) => {
    navigate({
      targetRoute: '/provider/localstore/' + bookingId + '/inventory',
      targetTitle: "Manage Store",
      targetData: {},
      sourceState: {},
      eventSource: '',
    });
  };

  const navToActivate = () => {
    navigate({
      targetRoute: '/provider/localstore/activate',
      targetTitle: "Activate Store",
      targetData: {},
      sourceState: {},
      eventSource: '',
    });
  };

  const userId = localstorage.getItem("userId");

  // Tabs: 0 Book Stalls · 1 My Stalls · 2 History · 3 Pending Approvals · 4 My Orders
  const [activeTab, setActiveTab] = useState(0);
  const handleTabChange = (_e: React.ChangeEvent<{}>, v: number) => setActiveTab(v);

  // ── Book Stalls tab ──────────────────────────────────────────────
  const [communities, setCommunities] = useState<SpecialCommunityOption[]>([]);
  const [selectedCommunityId, setSelectedCommunityId] = useState<number>(0);
  const [loadingCommunities, setLoadingCommunities] = useState(true);

  const [hourlyRate, setHourlyRate] = useState<number>(DEFAULT_HOURLY_RATE);

  // -- Calendar-driven slot selection --
  const [calendarSlots, setCalendarSlots] = useState<CalendarSlot[]>([]);
  // Every date currently in the calendar's selected range, regardless of whether a
  // slot has been picked for it yet — used to make sure no date gets left out.
  const [selectedRangeDates, setSelectedRangeDates] = useState<string[]>([]);

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [amountPaid, setAmountPaid] = useState<string>('');
  const [referenceNumber, setReferenceNumber] = useState<string>('');
  const [amountError, setAmountError] = useState<string>('');
  const [referenceError, setReferenceError] = useState<string>('');
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [appliedCouponCode, setAppliedCouponCode] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const paymentContentRef = useRef<HTMLDivElement | null>(null);

  // -- Payment screenshot upload --
  const [paymentScreenshotFile, setPaymentScreenshotFile] = useState<File | null>(null);
  const [paymentScreenshotPreview, setPaymentScreenshotPreview] = useState<string | null>(null);
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // ── My Stalls tab ────────────────────────────────────────────────
  const [myStalls, setMyStalls] = useState<StallOption[]>([]);
  const [loadingMyStalls, setLoadingMyStalls] = useState(false);

  // ── History tab ──────────────────────────────────────────────────
  const [stallHistory, setStallHistory] = useState<StallOption[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // ── Analytics dialog ─────────────────────────────────────────────
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analyticsBookingId, setAnalyticsBookingId] = useState<number>(0);
  const [analyticsStoreName, setAnalyticsStoreName] = useState<string>('');

  const openAnalytics = (store: StallOption) => {
    setAnalyticsBookingId(store.id);
    setAnalyticsStoreName(store.storeName || store.communityName);
    setAnalyticsOpen(true);
  };

  // ── Pending Approvals tab ────────────────────────────────────────
  const [pendingApprovals, setPendingApprovals] = useState<PendingApprovalOption[]>([]);
  const [loadingPending, setLoadingPending] = useState(false);

  // ── My Orders tab (across every stall this merchant has run) ─────
  const [merchantOrders, setMerchantOrders] = useState<MerchantOrder[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [orderSort, setOrderSort] = useState<'newest' | 'oldest'>('newest');
  const [orderFulfilmentFilter, setOrderFulfilmentFilter] = useState<'all' | 'Pickup' | 'Delivery'>('all');
  const [orderTimeFilter, setOrderTimeFilter] = useState<'all' | 'today' | 'lastHour'>('all');
  const [orderStallFilter, setOrderStallFilter] = useState<number | 'all'>('all');
  const [, forceTick] = useState(0);

  // Re-render every second so countdown timers on Reserved orders count down
  // live, only while the My Orders tab is actually open.
  useEffect(() => {
    if (activeTab !== 4) return;
    const tickInterval = setInterval(() => forceTick(t => t + 1), 1000);
    return () => clearInterval(tickInterval);
  }, [activeTab]);

  // Separately, while the My Orders tab is open, silently refetch every 30s
  // so a newly-placed order shows up without leaving and coming back to the tab.
  useEffect(() => {
    if (activeTab !== 4) return;
    const pollInterval = setInterval(() => getMerchantOrders(true), 30000);
    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const getSpecialCommunities = () => {
    setLoadingCommunities(true);
    http.post({ url: `community/special/list`, data: { filters: {} } }).then(res => {
      const result: SpecialCommunityOption[] = res.data || [];
      setCommunities(result);
      if (result.length > 0) setSelectedCommunityId(result[0].id);
    }).catch(() => setCommunities([]))
      .finally(() => setLoadingCommunities(false));
  };

  const getHourlyRate = () => {
    if (!selectedCommunityId) return;
    http.get({ url: `localstore/community/${selectedCommunityId}/hourlyrate` })
      .then((res: any) => setHourlyRate(res.data?.rate || DEFAULT_HOURLY_RATE))
      .catch(() => setHourlyRate(DEFAULT_HOURLY_RATE));
  };

  useEffect(() => { getSpecialCommunities(); }, []);
  useEffect(() => { getHourlyRate(); /* eslint-disable-next-line */ }, [selectedCommunityId]);

  // Reset picked slots whenever the community changes, since slots are community-scoped
  useEffect(() => {
    setCalendarSlots([]);
    setCouponCode('');
    setCouponError('');
    setCouponDiscount(0);
    setAppliedCouponCode('');
  }, [selectedCommunityId]);

  // Dialogs can inherit odd scroll positions from focus/autofill behavior in some
  // browsers — force the payment modal to always open scrolled to the top.
  useEffect(() => {
    if (paymentModalOpen && paymentContentRef.current) {
      paymentContentRef.current.scrollTop = 0;
    }
  }, [paymentModalOpen]);

  // ── Totals ───────────────────────────────────────────────────────
  const totalHours = calendarSlots.reduce((sum, s) => sum + s.durationHours, 0);
  const totalAmount = totalHours * hourlyRate;
  const finalAmount = Math.max(0, totalAmount - couponDiscount);

  // Dates the user has included in their range but hasn't picked a time slot for yet.
  const datesMissingSlots = selectedRangeDates.filter(
    d => !calendarSlots.some(s => s.date === d)
  );

  const canProceed =
    selectedCommunityId > 0 &&
    selectedRangeDates.length > 0 &&
    calendarSlots.length > 0 &&
    datesMissingSlots.length === 0;

  // ── My Stalls / History / Pending ───────────────────────────────
  const getMyStalls = () => {
    if (!userId) return;
    setLoadingMyStalls(true);
    http.get({ url: `localstore/user/${userId}/approved` })
      .then((res: any) => setMyStalls(res.data?.stores || []))
      .catch(() => setMyStalls([]))
      .finally(() => setLoadingMyStalls(false));
  };

  const getStallHistory = () => {
    if (!userId) return;
    setLoadingHistory(true);
    http.get({ url: `localstore/user/${userId}/history` })
      .then((res: any) => setStallHistory(res.data?.stores || []))
      .catch(() => setStallHistory([]))
      .finally(() => setLoadingHistory(false));
  };

  const getPendingApprovals = () => {
    if (!userId) return;
    setLoadingPending(true);
    http.get({ url: `localstore/user/${userId}/pending` })
      .then((res: any) => setPendingApprovals(res.data?.bookings || []))
      .catch(() => setPendingApprovals([]))
      .finally(() => setLoadingPending(false));
  };

  // `silent` skips the loading spinner — used by the background poll below so
  // a freshly-arrived order doesn't flash the whole list to a spinner.
  const getMerchantOrders = (silent = false) => {
    if (!userId) return;
    if (!silent) setLoadingOrders(true);
    http.get({ url: `localstore/user/${userId}/orders` })
      .then((res: any) => setMerchantOrders(res.data?.orders || []))
      .catch(() => { if (!silent) setMerchantOrders([]); })
      .finally(() => { if (!silent) setLoadingOrders(false); });
  };

  const isValidDate = (value?: string) => !!value && !Number.isNaN(new Date(value).getTime());

  // Cart checkout creates one orderGroupId for all lines in that checkout.
  // Older records without a group ID use their reservation ID as a fallback so
  // they remain separate instead of being incorrectly combined together.
  const groupedOrders = React.useMemo(() => {
    const groups = new Map<string, MerchantOrder[]>();
    merchantOrders.forEach(order => {
      const groupId = order.orderGroupId?.trim() || `reservation-${order.id}`;
      const lines = groups.get(groupId) || [];
      lines.push(order);
      groups.set(groupId, lines);
    });

    const result = Array.from(groups.entries()).map(([groupId, lines]) => {
      const firstLine = lines[0];
      const expiresAt = lines.map(line => line.expiresAt).find(isValidDate);
      return {
        groupId,
        lines,
        // orderGroupId (a GUID) is only needed for grouping — shown to the
        // merchant as the numeric id of its earliest reservation line instead.
        orderLabel: String(Math.min(...lines.map(line => line.id))),
        reservedDate: lines.reduce((latest, line) => line.reservedDate > latest ? line.reservedDate : latest, firstLine.reservedDate),
        expiresAt,
        status: firstLine.status,
        // A line can expire independently of its siblings (only still-"Reserved"
        // lines get swept), so an order isn't safe to act on unless every line
        // in it is still Reserved — otherwise the merchant could mark an
        // already-expired/released item as picked up or a no-show.
        canAct: lines.every(line => line.status === 'Reserved'),
        fulfilmentType: firstLine.fulfilmentType,
        buyerName: firstLine.buyerName,
        buyerMobile: firstLine.buyerMobile,
        buyerFlatNumber: firstLine.buyerFlatNumber,
        storeName: firstLine.storeName,
        communityName: firstLine.communityName,
        localStoreBookingId: firstLine.localStoreBookingId,
        // Lines with no price snapshot ("Ask price" items) are left out of the
        // total rather than treated as ₹0, so the total never understates
        // what's actually owed for a mixed-price order.
        totalAmount: lines.reduce((sum, line) => sum + (line.price != null ? line.price * line.quantity : 0), 0),
        hasUnpricedLine: lines.some(line => line.price == null),
      };
    });

    result.sort((a, b) => orderSort === 'newest'
      ? new Date(b.reservedDate).getTime() - new Date(a.reservedDate).getTime()
      : new Date(a.reservedDate).getTime() - new Date(b.reservedDate).getTime());
    return result;
  }, [merchantOrders, orderSort]);

  // Distinct stalls this merchant has run, for the stall filter dropdown.
  const stallFilterOptions = React.useMemo(() => {
    const byId = new Map<number, string>();
    merchantOrders.forEach(o => {
      if (!byId.has(o.localStoreBookingId)) {
        byId.set(o.localStoreBookingId, `${o.storeName} · ${o.communityName}`);
      }
    });
    return Array.from(byId.entries()).map(([id, label]) => ({ id, label }));
  }, [merchantOrders]);

  const filteredOrders = React.useMemo(() => {
    const now = Date.now();
    return groupedOrders.filter(group => {
      if (orderFulfilmentFilter !== 'all' && group.fulfilmentType !== orderFulfilmentFilter) return false;
      if (orderStallFilter !== 'all' && group.localStoreBookingId !== orderStallFilter) return false;
      if (orderTimeFilter !== 'all') {
        const reservedTime = new Date(group.reservedDate).getTime();
        if (orderTimeFilter === 'lastHour' && now - reservedTime > 60 * 60 * 1000) return false;
        if (orderTimeFilter === 'today' && new Date(group.reservedDate).toDateString() !== new Date(now).toDateString()) return false;
      }
      return true;
    });
  }, [groupedOrders, orderFulfilmentFilter, orderTimeFilter, orderStallFilter]);

  const formatCountdown = (expiresAt?: string) => {
    if (!isValidDate(expiresAt)) return null;
    const diffMs = new Date(expiresAt).getTime() - Date.now();
    if (diffMs <= 0) return 'Expired';
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);
    return `${mins}m ${secs}s left`;
  };

  const updateOrderGroupStatus = (group: { lines: MerchantOrder[] }, status: string) => {
    Promise.all(group.lines.map(order => http.post({
      url: `localstore/reservation/${order.id}/status`,
      data: { requestingBookingId: order.localStoreBookingId, status }
    })))
      .then((responses: any[]) => {
        const failedResponse = responses.find(response => !response.data?.success);
        if (failedResponse) {
          AlertService.showMsg({ message: failedResponse.data?.error || "Could not update status" });
          return;
        }
        getMerchantOrders();
      })
      .catch(() => AlertService.showMsg({ message: "Could not update status" }));
  };

  useEffect(() => {
    if (activeTab === 1) getMyStalls();
    if (activeTab === 2) getStallHistory();
    if (activeTab === 3) getPendingApprovals();
    if (activeTab === 4) getMerchantOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const openPaymentModal = () => {
    if (!selectedCommunityId) {
      AlertService.showMsg({ message: "Please select a community" });
      return;
    }
    if (selectedRangeDates.length === 0 || calendarSlots.length === 0) {
      AlertService.showMsg({ message: "Please select at least one slot" });
      return;
    }
    if (datesMissingSlots.length > 0) {
      AlertService.showMsg({
        message: `Please pick at least one time slot for each selected date — ${datesMissingSlots.length} date${datesMissingSlots.length > 1 ? 's' : ''} still need one.`
      });
      return;
    }
    // Pre-fill with the computed total. Applying a valid coupon can reduce it,
    // but merchants cannot manually alter the payable amount.
    setAmountPaid(String(finalAmount));
    setReferenceNumber('');
    setAmountError('');
    setReferenceError('');
    setPaymentScreenshotFile(null);
    setPaymentScreenshotPreview(null);
    setPaymentModalOpen(true);
  };

  const closePaymentModal = () => setPaymentModalOpen(false);

  const handleScreenshotPick = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    setPaymentScreenshotFile(file);
    setPaymentScreenshotPreview(URL.createObjectURL(file));
  };

  const handleScreenshotDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);
    handleScreenshotPick(e.dataTransfer.files);
  };

  const removeScreenshot = () => {
    setPaymentScreenshotFile(null);
    setPaymentScreenshotPreview(null);
  };

  const uploadPaymentScreenshot = async (): Promise<string | null> => {
    if (!paymentScreenshotFile) return null;
    setUploadingScreenshot(true);
    try {
      const presignRes: any = await http.post({
        url: `localstore/payment-screenshot/presign-image`,
        data: { fileName: paymentScreenshotFile.name, contentType: paymentScreenshotFile.type }
      });
      if (!presignRes.data?.success) throw new Error(presignRes.data?.error || 'Could not get upload URL');

      await fetch(presignRes.data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': paymentScreenshotFile.type },
        body: paymentScreenshotFile
      });

      return presignRes.data.publicUrl;
    } finally {
      setUploadingScreenshot(false);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only digits and a single decimal point — keeps mobile numeric
    // keyboards happy without the scroll/spinner quirks of type="number".
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmountPaid(value);
      if (amountError) setAmountError('');
    }
  };

  const applyCoupon = () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) {
      setCouponError('Enter a coupon code');
      return;
    }
    setValidatingCoupon(true);
    setCouponError('');
    http.post({
      url: `localstore/community/${selectedCommunityId}/coupons/validate`,
      data: { code, subtotal: totalAmount, userId: Number(userId) }
    })
      .then((res: any) => {
        if (!res.data?.success) {
          setCouponDiscount(0);
          setAppliedCouponCode('');
          setAmountPaid(String(totalAmount));
          setCouponError(res.data?.error || 'Invalid coupon code');
          return;
        }
        setCouponDiscount(Number(res.data.discountAmount) || 0);
        setAppliedCouponCode(res.data.code || code);
        setCouponCode(res.data.code || code);
        setAmountPaid(String(Number(res.data.finalAmount)));
      })
      .catch(() => setCouponError('Could not validate coupon. Please try again.'))
      .finally(() => setValidatingCoupon(false));
  };

  const removeAppliedCoupon = () => {
    setCouponCode('');
    setCouponError('');
    setCouponDiscount(0);
    setAppliedCouponCode('');
    setAmountPaid(String(totalAmount));
  };

  const handleBookingSlotsChange = (slots: CalendarSlot[]) => {
    setCalendarSlots(slots);
    if (appliedCouponCode) {
      setCouponCode('');
      setCouponError('');
      setCouponDiscount(0);
      setAppliedCouponCode('');
    }
  };

  const handleSubmitPayment = async () => {
    const parsedAmount = parseFloat(amountPaid);
    let hasError = false;

    if (isNaN(parsedAmount) || parsedAmount < 0 || Math.abs(parsedAmount - finalAmount) > 0.009) {
      setAmountError("Enter a valid amount");
      hasError = true;
    } else {
      setAmountError('');
    }

    if (finalAmount > 0 && !referenceNumber.trim()) {
      setReferenceError("Enter the payment reference number");
      hasError = true;
    } else {
      setReferenceError('');
    }

    if (hasError) return;

    setSubmitting(true);
    try {
      const screenshotUrl = await uploadPaymentScreenshot();

      const res: any = await http.post({
        url: `localstore/register/custom`,
        data: {
          communityId: selectedCommunityId,
          userId: localstorage.getItem("userId"),
          slots: calendarSlots.map(s => ({ date: s.date, startHour: s.startHour, durationHours: s.durationHours })),
          amountPaid: parsedAmount,
          referenceNumber: referenceNumber.trim(),
          paymentScreenshotUrl: screenshotUrl,
          couponCode: appliedCouponCode || null
        }
      });

      const result = res.data;
      if (result && result.success) {
        AlertService.showSuccessMsg("Booking submitted! It will be confirmed once the admin approves your payment.");
        setPaymentModalOpen(false);
        setCalendarSlots([]);
        setPaymentScreenshotFile(null);
        setPaymentScreenshotPreview(null);
        back();
      } else {
        const code = result?.error;
        AlertService.showMsg({ message: REGISTER_ERROR_MESSAGES[code] || code || "Could not submit booking. Please try again." });
        if (code === 'SLOT_IN_PAST' || code === 'SLOT_TAKEN') {
          setCalendarSlots([]); // force them to re-pick rather than resubmit stale state
        }
      }
    } catch {
      AlertService.showMsg({ message: "Something went wrong. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCommunityName = communities.find(c => c.id === selectedCommunityId)?.text;

  return (
    <div className="localstore-registration-ui">
      <div className="header-card">
        <div className="awning-stripe" />
        <div className="page-header">
          <Typography variant="h5" className="page-title">Local Store</Typography>
          <Typography className="page-subtitle">
           
          </Typography>
        </div>

        <div className="tabs-wrap">
          <Tabs value={activeTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto">
            <Tab label="Book Stalls" />
            <Tab label="My Stalls" />
            <Tab label="History" />
            <Tab label="Pending Approvals" />
            <Tab label="My Orders" />
          </Tabs>
        </div>
      </div>

      {/* ── BOOK STALLS ─────────────────────────────────────────── */}
      {activeTab === 0 && (
        <Grid container spacing={3} className="content">
          <Grid item xs={12} md={7}>
            <Box className="panel-card">
              <FormControl fullWidth variant="outlined" size="small" className="mb-3">
                <InputLabel>Special Community</InputLabel>
                <Select value={selectedCommunityId} onChange={(e) => setSelectedCommunityId(e.target.value as number)} label="Special Community" disabled={loadingCommunities}>
                  {communities.length === 0 && !loadingCommunities && (
                    <MenuItem value={0} disabled>No active special communities available</MenuItem>
                  )}
                  {communities.map((c) => <MenuItem key={c.id} value={c.id}>{c.text}</MenuItem>)}
                </Select>
              </FormControl>

              {/* LocalStoreBookingCalendar renders its own "Selected slots" list at the
                  bottom of the calendar card, so nothing else needs to duplicate it here. */}
              <LocalStoreBookingCalendar
                communityId={selectedCommunityId}
                minDate={formatService.getCurrentDateForServer()}
                onSlotsChange={handleBookingSlotsChange}
                onRangeChange={setSelectedRangeDates}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box className="panel-card summary-card">
              <Typography className="summary-title">Booking Summary</Typography>
              <div className="pt-2">
                <div className="summary-row">
                  <span className="label">Community</span>
                  <span className="value">{selectedCommunityName || '—'}</span>
                </div>
                <div className="summary-row">
                  <span className="label">Dates selected</span>
                  <span className="value">{selectedRangeDates.length || '—'}</span>
                </div>
                <div className="summary-row summary-row--column">
                  <span className="label">Slots</span>
                </div>
                <div className="summary-slot-breakdown">
                  {calendarSlots.length === 0 && <span className="summary-slot-empty">None selected yet</span>}
                  {calendarSlots.map((s, i) => (
                    <div key={i} className="summary-slot-line">{s.label}</div>
                  ))}
                </div>
                <div className="summary-row">
                  <span className="label">Rate</span>
                  <span className="value">₹{hourlyRate}/hr</span>
                </div>
                <div className="summary-coupon">
                  <div className="summary-coupon-label">Have a coupon?</div>
                  <div className="summary-coupon-controls">
                    <TextField
                      placeholder="Enter coupon code"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase());
                        setCouponError('');
                      }}
                      disabled={!!appliedCouponCode || !canProceed}
                      error={!!couponError}
                      helperText={couponError || (appliedCouponCode ? `Applied — ₹${couponDiscount.toLocaleString('en-IN')} off` : ' ')}
                    />
                    <Button
                      variant="outlined"
                      onClick={appliedCouponCode ? removeAppliedCoupon : applyCoupon}
                      disabled={validatingCoupon || (!appliedCouponCode && (!canProceed || !couponCode.trim()))}
                    >
                      {validatingCoupon ? <CircularProgress size={16} /> : appliedCouponCode ? 'Remove' : 'Apply'}
                    </Button>
                  </div>
                </div>
                {couponDiscount > 0 && (
                  <div className="summary-row summary-discount-row">
                    <span className="label">Coupon discount</span>
                    <span className="value">−₹{couponDiscount.toLocaleString('en-IN')}</span>
                  </div>
                )}
                <div className="total-row">
                  <span>Total</span>
                  <span>₹{finalAmount.toLocaleString('en-IN')}</span>
                </div>
              </div>

              {datesMissingSlots.length > 0 && calendarSlots.length > 0 && (
                <Typography className="rejected-reason">
                  {datesMissingSlots.length} of your selected date{datesMissingSlots.length > 1 ? 's' : ''} still need{datesMissingSlots.length > 1 ? '' : 's'} a time slot before you can continue.
                </Typography>
              )}

              <Button
                variant="contained"
                className="primary-btn"
                fullWidth
                disabled={!canProceed}
                onClick={openPaymentModal}
              >
                Register &amp; Pay <ArrowForwardIcon fontSize="small" />
              </Button>

              <Typography className="register-note">
                After payment, your booking stays pending until the community admin approves it.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* ── MY STALLS ───────────────────────────────────────────── */}
      {activeTab === 1 && (
        <div className="content">
          {loadingMyStalls && (
            <Box display="flex" justifyContent="center" py={5}><CircularProgress size={24} /></Box>
          )}

          {!loadingMyStalls && myStalls.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon-badge empty-icon-badge--lg"><StorefrontIcon /></div>
              <Typography className="empty-title">No active stalls yet</Typography>
              <Typography className="empty-detail">Once an admin approves your payment, it'll show up here.</Typography>
            </div>
          )}

          {!loadingMyStalls && myStalls.length > 0 && (
            <Grid container spacing={2}>
              {myStalls.map((store) => (
                <Grid item xs={12} sm={6} md={4} key={store.id}>
                  <div className="stall-card">
                    <div className="stall-header-row">
                      <div className="stall-icon-badge"><StorefrontIcon /></div>
                      <div>
                        <Typography className="stall-name">{store.storeName || store.communityName}</Typography>
                        <span className="status-chip status-chip--active">{store.bookingType}</span>
                      </div>
                    </div>

                    <div className="slot-list">
                      {store.slots.map((s, i) => (
                        <div key={i} className="meta-row">
                          <EventIcon className="meta-icon" /> {s.slotDate} · {s.slotLabel}
                        </div>
                      ))}
                    </div>

                    <Button variant="contained" className="manage-store-btn" onClick={() => navToManageStore(store.id)}>
                      Manage Store
                    </Button>
                    <Button variant="text" className="activate-link-btn" onClick={navToActivate}>
                      Activate Store
                    </Button>
                  </div>
                </Grid>
              ))}
            </Grid>
          )}
        </div>
      )}

      {/* ── HISTORY ─────────────────────────────────────────────── */}
      {activeTab === 2 && (
        <div className="content">
          {loadingHistory && (
            <Box display="flex" justifyContent="center" py={5}><CircularProgress size={24} /></Box>
          )}

          {!loadingHistory && stallHistory.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon-badge empty-icon-badge--lg"><HistoryIcon /></div>
              <Typography className="empty-title">No past stalls yet</Typography>
              <Typography className="empty-detail">Stalls you've completed will show up here, along with their performance.</Typography>
            </div>
          )}

          {!loadingHistory && stallHistory.length > 0 && (
            <Grid container spacing={2}>
              {stallHistory.map((store) => (
                <Grid item xs={12} sm={6} md={4} key={store.id}>
                  <div
                    className="history-card"
                    onClick={() => openAnalytics(store)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="stall-header-row">
                      <div className="stall-icon-badge stall-icon-badge--muted"><StorefrontIcon /></div>
                      <div>
                        <Typography className="stall-name">{store.storeName || store.communityName}</Typography>
                        <span className="status-chip status-chip--muted">{store.bookingType}</span>
                      </div>
                    </div>

                    <div className="slot-list">
                      {store.slots.map((s, i) => (
                        <div key={i} className="meta-row">
                          <EventIcon className="meta-icon" /> {s.slotDate} · {s.slotLabel}
                        </div>
                      ))}
                    </div>

                    <div className="history-view-hint">
                      View analytics <ArrowForwardIcon fontSize="small" />
                    </div>
                  </div>
                </Grid>
              ))}
            </Grid>
          )}
        </div>
      )}

      {/* ── PENDING APPROVALS ───────────────────────────────────── */}
      {activeTab === 3 && (
        <div className="content">
          {loadingPending && <Box display="flex" justifyContent="center" py={5}><CircularProgress size={24} /></Box>}

          {!loadingPending && pendingApprovals.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon-badge"><HourglassEmptyIcon /></div>
              <Typography className="empty-title">No pending payments</Typography>
            </div>
          )}

          <Grid container spacing={2}>
            {!loadingPending && pendingApprovals.map((b) => (
              <Grid item xs={12} key={b.id}>
                <div className="pending-card">
                  <div>
                    <Typography className="pending-name">{b.communityName} · {b.bookingType}</Typography>
                    <Typography className="pending-meta">
                      {b.slots.length} slot{b.slots.length !== 1 ? 's' : ''} · ₹{b.amountPaid.toLocaleString('en-IN')}
                    </Typography>
                    {b.status === 'Rejected' && b.rejectedReason && (
                      <Typography className="rejected-reason">Reason: {b.rejectedReason}</Typography>
                    )}
                  </div>
                  <span className={
                    b.status === 'Rejected' ? 'status-chip status-chip--rejected' :
                    b.status === 'Paid' ? 'status-chip status-chip--approved' :
                    'status-chip status-chip--pending'
                  }>
                    {b.status === 'Paid' ? 'Approved' : b.status}
                  </span>
                </div>
              </Grid>
            ))}
          </Grid>
        </div>
      )}

      {/* ── MY ORDERS (across every stall this merchant has run) ────── */}
      {activeTab === 4 && (
        <div className="content">
          <div className="orders-toolbar">
            <FormControl size="small" variant="outlined" className="sort-field">
              <InputLabel>Sort</InputLabel>
              <Select value={orderSort} onChange={(e) => setOrderSort(e.target.value as 'newest' | 'oldest')} label="Sort">
                <MenuItem value="newest">Newest first</MenuItem>
                <MenuItem value="oldest">Oldest first</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" variant="outlined" className="sort-field">
              <InputLabel>Fulfilment</InputLabel>
              <Select value={orderFulfilmentFilter} onChange={(e) => setOrderFulfilmentFilter(e.target.value as 'all' | 'Pickup' | 'Delivery')} label="Fulfilment">
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="Pickup">Pickup</MenuItem>
                <MenuItem value="Delivery">Delivery</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" variant="outlined" className="sort-field">
              <InputLabel>When</InputLabel>
              <Select value={orderTimeFilter} onChange={(e) => setOrderTimeFilter(e.target.value as 'all' | 'today' | 'lastHour')} label="When">
                <MenuItem value="all">All time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="lastHour">Last hour</MenuItem>
              </Select>
            </FormControl>

            {stallFilterOptions.length > 1 && (
              <FormControl size="small" variant="outlined" className="sort-field">
                <InputLabel>Stall</InputLabel>
                <Select value={orderStallFilter} onChange={(e) => setOrderStallFilter(e.target.value as number | 'all')} label="Stall">
                  <MenuItem value="all">All stalls</MenuItem>
                  {stallFilterOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>

          {loadingOrders && <Box display="flex" justifyContent="center" py={5}><CircularProgress size={24} /></Box>}

          {!loadingOrders && filteredOrders.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon-badge"><StorefrontIcon /></div>
              <Typography className="empty-title">No orders {groupedOrders.length === 0 ? 'yet' : 'match these filters'}</Typography>
              <Typography className="empty-detail">
                {groupedOrders.length === 0
                  ? 'Reservations from any of your stalls will show up here.'
                  : 'Try widening your filters above.'}
              </Typography>
            </div>
          )}

          {!loadingOrders && filteredOrders.map((group) => {
            // The server only flips a reservation's status to "Expired" the
            // next time something triggers the lazy sweep — it doesn't happen
            // the instant the clock hits zero. So the countdown reaching zero
            // has to disable these buttons itself, right here in render (which
            // re-runs every second from the tab's tick), rather than waiting
            // on group.canAct / group.status, which only refresh on the 30s poll.
            const isPastExpiry = group.status === 'Reserved' && group.fulfilmentType !== 'Delivery'
              && !!group.expiresAt && new Date(group.expiresAt).getTime() <= Date.now();
            const actionsDisabled = !group.canAct || isPastExpiry;
            const displayStatus = isPastExpiry ? 'Expired' : group.status;

            return (
            <div className="order-card" key={group.groupId}>
              <div className="order-card-header">
                <span className="order-id-badge">Order #{group.orderLabel} · {group.lines.length} item{group.lines.length !== 1 ? 's' : ''}</span>
                <span className="order-stall-name">{group.storeName} · {group.communityName}</span>
                <div className="order-header-right">
                  {group.status === 'Reserved' && !isPastExpiry && group.fulfilmentType !== 'Delivery' && group.expiresAt && (
                    <span className="order-countdown">{formatCountdown(group.expiresAt)}</span>
                  )}
                  <span className={`order-status-chip order-status-${displayStatus.toLowerCase()}`}>
                    {displayStatus === 'NoShow' && group.fulfilmentType === 'Delivery'
                      ? 'Delivery failed'
                      : (ORDER_STATUS_LABELS[displayStatus] || displayStatus)}
                  </span>
                </div>
              </div>

              <div className="order-amount-breakdown">
                {group.lines.map(order => (
                  <div className="order-amount-line" key={order.id}>
                    <span className="order-product-name">
                      {order.productName} <span className="order-meta">× {order.quantity}</span>
                    </span>
                    <span className="order-amount-line-value">
                      {order.price != null ? `₹${(order.price * order.quantity).toLocaleString('en-IN')}` : 'Ask price'}
                    </span>
                  </div>
                ))}
                {group.totalAmount > 0 ? (
                  <div className="order-amount-total">
                    <span>Total{group.hasUnpricedLine ? ' (excl. ask-price items)' : ''}</span>
                    <span className="order-amount-total-value">₹{group.totalAmount.toLocaleString('en-IN')}</span>
                  </div>
                ) : (
                  <div className="order-amount-total order-amount-total-unpriced">
                    <span>All items priced on request</span>
                  </div>
                )}
              </div>

              <div className="order-card-footer">
                <div className="order-card-meta">
                  <div className="order-meta">
                    {group.buyerName}{group.buyerFlatNumber ? ` (Flat ${group.buyerFlatNumber})` : ''} · {group.fulfilmentType} · {new Date(group.reservedDate).toLocaleDateString()}
                  </div>
                  {group.buyerMobile && <div className="order-mobile">{group.buyerMobile}</div>}
                </div>
                {group.status === 'Reserved' && (
                  <div className="order-action-buttons">
                    {group.fulfilmentType === 'Pickup' ? (
                      <button className="order-action-btn order-action-good" onClick={() => updateOrderGroupStatus(group, 'PickedUp')} disabled={actionsDisabled}>Picked up</button>
                    ) : (
                      <button className="order-action-btn order-action-good" onClick={() => updateOrderGroupStatus(group, 'Delivered')} disabled={actionsDisabled}>Delivered</button>
                    )}
                    {group.fulfilmentType === 'Pickup' ? (
                      <button className="order-action-btn order-action-bad" onClick={() => updateOrderGroupStatus(group, 'NoShow')} disabled={actionsDisabled}>Customer didn't show up</button>
                    ) : (
                      <button className="order-action-btn order-action-bad" onClick={() => updateOrderGroupStatus(group, 'NoShow')} disabled={actionsDisabled}>Delivery failed</button>
                    )}
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* ── Payment Modal ────────────────────────────────────────── */}
      <Dialog open={paymentModalOpen} onClose={closePaymentModal} maxWidth="sm" fullWidth className="payment-dialog-v2">
        <div className="pd-header">
          <div className="pd-header-stripe" />
          <IconButton size="small" onClick={closePaymentModal} className="pd-close-btn" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
          <div className="pd-header-content">
            <Typography className="pd-header-eyebrow">Complete your booking</Typography>
            <Typography className="pd-header-title">₹{finalAmount.toLocaleString('en-IN')}</Typography>
            <Typography className="pd-header-sub">
              {selectedCommunityName} · {totalHours} hour{totalHours !== 1 ? 's' : ''} · {selectedRangeDates.length} date{selectedRangeDates.length !== 1 ? 's' : ''}
            </Typography>
          </div>
        </div>

        <DialogContent className="pd-content" ref={paymentContentRef}>
          <div className="pd-section">
            <div className="pd-section-label">
              <PaymentIcon fontSize="small" /> Scan &amp; pay
            </div>
            <div className="pd-qr-card">
              <img src={PAYMENT_QR_IMAGE} alt="Payment QR" className="pd-qr-image" width="150" height="150" />
              <Typography className="pd-qr-caption">
                Pay <b>₹{finalAmount.toLocaleString('en-IN')}</b> using any UPI app — GPay, PhonePe, Paytm, or your bank's app.
              </Typography>
              <Typography className="pd-qr-hint">Tip: press and hold the code to save it to your gallery.</Typography>
            </div>
          </div>

          <div className="pd-section">
            <div className="pd-section-label">
              <ReceiptIcon fontSize="small" /> Payment details
            </div>
            <TextField
              label="Amount Paid"
              variant="outlined"
              size="small"
              fullWidth
              margin="dense"
              className="pd-field"
              value={amountPaid}
              onChange={handleAmountChange}
              inputProps={{ inputMode: 'decimal' }}
              error={!!amountError}
              helperText={amountError || ' '}
              InputProps={{
                readOnly: true,
                startAdornment: <InputAdornment position="start">₹</InputAdornment>
              }}
            />
            <TextField
              label="Payment Reference Number"
              variant="outlined"
              size="small"
              fullWidth
              margin="dense"
              className="pd-field"
              value={referenceNumber}
              onChange={(e) => {
                setReferenceNumber(e.target.value);
                if (referenceError) setReferenceError('');
              }}
              error={!!referenceError}
              helperText={referenceError || "Find this in your UPI app's transaction history"}
            />
          </div>

          <div className="pd-section pd-section--last">
            <div className="pd-section-label">
              <CloudUploadIcon fontSize="small" /> Payment screenshot
              <span className="pd-optional-tag">Optional</span>
            </div>
            <Typography className="pd-step-hint">Helps the admin verify your payment faster.</Typography>

            {!paymentScreenshotPreview ? (
              <label
                className={`pd-upload-zone${isDraggingOver ? ' pd-upload-zone--dragging' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
                onDragLeave={() => setIsDraggingOver(false)}
                onDrop={handleScreenshotDrop}
              >
                <CloudUploadIcon />
                <span>Tap to upload, or drag a screenshot here</span>
                <input type="file" accept="image/*" hidden onChange={(e) => handleScreenshotPick(e.target.files)} />
              </label>
            ) : (
              <div className="pd-screenshot-preview">
                <img src={paymentScreenshotPreview} alt="Payment screenshot" />
                <IconButton size="small" className="pd-screenshot-remove" onClick={removeScreenshot} aria-label="Remove screenshot">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions className="pd-actions">
          <Button
            fullWidth
            variant="contained"
            className="pd-submit-btn"
            onClick={handleSubmitPayment}
            disabled={submitting || uploadingScreenshot}
          >
            {submitting || uploadingScreenshot
              ? <CircularProgress size={18} className="pd-btn-spinner" />
              : 'Submit for Approval'}
          </Button>
          <Typography className="pd-footnote">
            Your booking stays pending until the community admin approves your payment.
          </Typography>
        </DialogActions>
      </Dialog>

      <LocalStoreAnalytics
        open={analyticsOpen}
        bookingId={analyticsBookingId}
        storeName={analyticsStoreName}
        onClose={() => setAnalyticsOpen(false)}
      />
    </div>
  );
};

export default LocalStoreRegistration;
